import * as React from 'react';
import type { Block } from '@pageboss/shared';
import { EditableText } from './editable-text';
import { IconPreview } from './inspector/icon-picker';

// Renders each of the 14 block types at editor-fidelity.
//
// Modes:
// - editable=false (default): pure preview, no chrome, no edit affordances.
// - editable=true: inline contenteditable on text-heavy fields (hero,
//   pricing, about, cta, heading, text, faq, footer). The 6
//   inspector-only blocks (services, credentials, gallery,
//   testimonials, team, contact) surface their full editing in the
//   right-side panel — their preview here is read-only.
//
// onChange receives the FULL next props object for the block. The
// caller (page-edit autosave hook) does setBlock(id, nextProps).

interface BlockPreviewProps {
  block: Block;
  editable?: boolean;
  onChange?: (nextProps: Record<string, unknown>) => void;
}

// Memoized: the builder re-renders the whole page state on every
// keystroke, but unchanged blocks keep their `block` object reference
// (updateBlockProps maps immutably). With a stable `onChange` (the
// SortableBlock wraps it in a ref-backed callback), this skips the
// expensive content + nested-EditableText subtree for every block
// except the one being edited.
export const BlockPreview = React.memo(function BlockPreview({
  block,
  editable = false,
  onChange,
}: BlockPreviewProps) {
  switch (block.type) {
    case 'hero':
      return <HeroBlock block={block} editable={editable} onChange={onChange} />;
    case 'services':
      return <ServicesBlock block={block} />;
    case 'pricing':
      return <PricingBlock block={block} editable={editable} onChange={onChange} />;
    case 'gallery':
      return <GalleryBlock block={block} />;
    case 'contact':
      return <ContactBlock block={block} />;
    case 'about':
      return <AboutBlock block={block} editable={editable} onChange={onChange} />;
    case 'testimonials':
      return <TestimonialsBlock block={block} />;
    case 'cta':
      return <CtaBlock block={block} editable={editable} onChange={onChange} />;
    case 'heading':
      return <HeadingBlock block={block} editable={editable} onChange={onChange} />;
    case 'text':
      return <TextBlock block={block} editable={editable} onChange={onChange} />;
    case 'faq':
      return <FaqBlock block={block} editable={editable} onChange={onChange} />;
    case 'team':
      return <TeamBlock block={block} />;
    case 'credentials':
      return <CredentialsBlock block={block} />;
    case 'locations':
      return <LocationsBlock block={block} />;
    case 'footer':
      return <FooterBlock block={block} editable={editable} onChange={onChange} />;
  }
});

// =====================================================================
// Shared bits
// =====================================================================

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-700">{children}</p>
  );
}

function PrimaryPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
      {children}
    </span>
  );
}

function SecondaryPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-primary px-4 py-2 text-sm font-semibold text-primary">
      {children}
    </span>
  );
}

function ImagePlaceholder({ alt }: { alt?: string }) {
  return (
    <div
      role="img"
      aria-label={alt || 'Image'}
      className="flex aspect-[4/3] items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground"
    >
      <span>{alt || 'Image'}</span>
    </div>
  );
}

// =====================================================================
// Hero
// =====================================================================

type HeroProps = Extract<Block, { type: 'hero' }>['props'];

function HeroBlock({
  block,
  editable,
  onChange,
}: {
  block: Extract<Block, { type: 'hero' }>;
  editable: boolean;
  onChange: BlockPreviewProps['onChange'];
}) {
  const props = block.props;
  const update = (patch: Partial<HeroProps>) => onChange?.({ ...props, ...patch });
  const split = props.layout === 'split-left' || props.layout === 'split-right';
  const mediaOrder = props.layout === 'split-right' ? 'lg:order-last' : '';

  return (
    <div
      className={
        split
          ? 'grid items-center gap-6 @lg/col:gap-10 @lg/col:grid-cols-2'
          : 'mx-auto flex max-w-2xl flex-col items-center gap-4 text-center'
      }
    >
      <div className="space-y-4">
        {props.eyebrow || editable ? (
          editable ? (
            <EditableText
              value={props.eyebrow ?? ''}
              onChange={(v) => update({ eyebrow: v || undefined })}
              placeholder="Eyebrow text"
              className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-700"
            />
          ) : (
            <Eyebrow>{props.eyebrow}</Eyebrow>
          )
        ) : null}

        {editable ? (
          <EditableText
            value={props.headline}
            onChange={(v) => update({ headline: v })}
            placeholder="Tell people who you are."
            className="text-2xl @md/col:text-3xl @2xl/col:text-4xl font-semibold tracking-tight text-foreground"
          />
        ) : (
          <h1 className="text-2xl @md/col:text-3xl @2xl/col:text-4xl font-semibold tracking-tight text-foreground">
            {props.headline}
          </h1>
        )}

        {props.subhead || editable ? (
          editable ? (
            <EditableText
              value={props.subhead ?? ''}
              onChange={(v) => update({ subhead: v || undefined })}
              placeholder="A short line that adds detail."
              multiline
              className="text-base @md/col:text-lg leading-relaxed text-muted-foreground"
            />
          ) : (
            <p className="text-base @md/col:text-lg leading-relaxed text-muted-foreground">
              {props.subhead}
            </p>
          )
        ) : null}

        {props.primaryCta || props.secondaryCta ? (
          <div className={'flex flex-wrap gap-3 pt-2 ' + (split ? '' : 'justify-center')}>
            {props.primaryCta ? (
              editable ? (
                <EditableText
                  value={props.primaryCta.label}
                  onChange={(v) =>
                    update({ primaryCta: { ...props.primaryCta!, label: v } })
                  }
                  placeholder="Primary CTA"
                  className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
                />
              ) : (
                <PrimaryPill>{props.primaryCta.label}</PrimaryPill>
              )
            ) : null}
            {props.secondaryCta ? (
              editable ? (
                <EditableText
                  value={props.secondaryCta.label}
                  onChange={(v) =>
                    update({ secondaryCta: { ...props.secondaryCta!, label: v } })
                  }
                  placeholder="Secondary CTA"
                  className="inline-flex items-center rounded-md border border-primary px-4 py-2 text-sm font-semibold text-primary"
                />
              ) : (
                <SecondaryPill>{props.secondaryCta.label}</SecondaryPill>
              )
            ) : null}
          </div>
        ) : null}
      </div>
      {split && props.image ? (
        <div className={mediaOrder}>
          {props.image.url ? (
            <img
              src={props.image.url}
              alt={props.image.alt}
              className="aspect-[4/3] w-full rounded-lg object-cover"
            />
          ) : (
            <ImagePlaceholder alt={props.image.alt} />
          )}
        </div>
      ) : null}
    </div>
  );
}

// =====================================================================
// Services (complex — defer to inspector in 3B-3d)
// =====================================================================

function ServicesBlock({
  block,
}: {
  block: Extract<Block, { type: 'services' }>;
}) {
  const props = block.props;
  const colsClass =
    props.columns === 2
      ? '@md/col:grid-cols-2'
      : props.columns === 4
      ? '@md/col:grid-cols-2 @4xl/col:grid-cols-4'
      : '@md/col:grid-cols-2 @2xl/col:grid-cols-3';
  return (
    <div className="space-y-6">
      {props.title ? (
        <h2 className="text-center text-xl @md/col:text-2xl font-semibold tracking-tight">{props.title}</h2>
      ) : null}
      <div className={'grid gap-5 ' + colsClass}>
        {props.items.map((item, i) => (
          <div key={i} className="rounded-lg border border-border-subtle bg-card p-5">
            {item.icon ? (
              <div className="mb-3">
                <IconPreview value={item.icon} className="h-6 w-6 text-brand-700 [&_span]:text-2xl" />
              </div>
            ) : null}
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-base font-semibold tracking-tight">{item.title}</h3>
              {item.price ? (
                <span className="shrink-0 text-sm font-semibold text-brand-700">{item.price}</span>
              ) : null}
            </div>
            {item.description ? (
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {item.description}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// Pricing — tier cards with inline-editable name/price/description.
// Features list + tier add/remove + CTA editing defer to the inspector
// (3B-3d follow-up). Highlighted tier shows a "Most popular" badge and
// a sage ring.
// =====================================================================

type PricingProps = Extract<Block, { type: 'pricing' }>['props'];

function PricingBlock({
  block,
  editable,
  onChange,
}: {
  block: Extract<Block, { type: 'pricing' }>;
  editable: boolean;
  onChange: BlockPreviewProps['onChange'];
}) {
  const props = block.props;
  const update = (patch: Partial<PricingProps>) => onChange?.({ ...props, ...patch });
  const updateTier = (index: number, patch: Partial<PricingProps['tiers'][number]>) => {
    const tiers = props.tiers.map((t, i) => (i === index ? { ...t, ...patch } : t));
    update({ tiers });
  };
  const colsClass = props.columns === 2 ? '@md/col:grid-cols-2' : '@md/col:grid-cols-2 @2xl/col:grid-cols-3';

  return (
    <div className="space-y-6">
      {props.title || editable ? (
        editable ? (
          <EditableText
            value={props.title ?? ''}
            onChange={(v) => update({ title: v || undefined })}
            placeholder="Simple, transparent pricing"
            className="text-center text-xl @md/col:text-2xl font-semibold tracking-tight"
          />
        ) : props.title ? (
          <h2 className="text-center text-xl @md/col:text-2xl font-semibold tracking-tight">
            {props.title}
          </h2>
        ) : null
      ) : null}
      {props.subtitle || editable ? (
        editable ? (
          <EditableText
            value={props.subtitle ?? ''}
            onChange={(v) => update({ subtitle: v || undefined })}
            placeholder="Pick the plan that fits where you are today."
            className="mx-auto max-w-2xl text-center text-sm @md/col:text-base text-muted-foreground"
          />
        ) : props.subtitle ? (
          <p className="mx-auto max-w-2xl text-center text-sm @md/col:text-base text-muted-foreground">
            {props.subtitle}
          </p>
        ) : null
      ) : null}
      <div className={'grid gap-5 ' + colsClass}>
        {props.tiers.map((tier, i) => (
          <div
            key={i}
            className={
              'relative flex flex-col rounded-xl border bg-card p-6 ' +
              (tier.highlighted
                ? 'border-brand-500 shadow-md ring-1 ring-brand-500'
                : 'border-border-subtle')
            }
          >
            {tier.highlighted ? (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground">
                Most popular
              </span>
            ) : null}
            <div>
              {editable ? (
                <EditableText
                  value={tier.name}
                  onChange={(v) => updateTier(i, { name: v || tier.name })}
                  placeholder="Tier name"
                  className="text-sm font-semibold uppercase tracking-wide text-text-secondary"
                />
              ) : (
                <div className="text-sm font-semibold uppercase tracking-wide text-text-secondary">
                  {tier.name}
                </div>
              )}
              <div className="mt-2 flex items-baseline gap-1">
                {editable ? (
                  <EditableText
                    value={tier.price}
                    onChange={(v) => updateTier(i, { price: v || tier.price })}
                    placeholder="$29"
                    className="text-3xl @md/col:text-4xl font-bold tracking-tight text-foreground"
                  />
                ) : (
                  <span className="text-3xl @md/col:text-4xl font-bold tracking-tight text-foreground">
                    {tier.price}
                  </span>
                )}
                {tier.period || editable ? (
                  editable ? (
                    <EditableText
                      value={tier.period ?? ''}
                      onChange={(v) => updateTier(i, { period: v || undefined })}
                      placeholder="/mo"
                      className="text-sm text-muted-foreground"
                    />
                  ) : (
                    <span className="text-sm text-muted-foreground">{tier.period}</span>
                  )
                ) : null}
              </div>
              {tier.description || editable ? (
                editable ? (
                  <EditableText
                    value={tier.description ?? ''}
                    onChange={(v) => updateTier(i, { description: v || undefined })}
                    placeholder="One line about who this tier is for."
                    className="mt-3 text-sm leading-relaxed text-muted-foreground"
                  />
                ) : (
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {tier.description}
                  </p>
                )
              ) : null}
            </div>
            {tier.features.length > 0 ? (
              <ul className="mt-5 space-y-2 text-sm">
                {tier.features.map((feature, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <span aria-hidden="true" className="mt-0.5 text-brand-600">✓</span>
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="mt-6 pt-2">
              {tier.highlighted ? (
                <PrimaryPill>{tier.cta.label}</PrimaryPill>
              ) : (
                <SecondaryPill>{tier.cta.label}</SecondaryPill>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// Gallery (complex)
// =====================================================================

function GalleryBlock({
  block,
}: {
  block: Extract<Block, { type: 'gallery' }>;
}) {
  const props = block.props;
  return (
    <div className="space-y-6">
      {props.title ? (
        <h2 className="text-center text-xl @md/col:text-2xl font-semibold tracking-tight">{props.title}</h2>
      ) : null}
      <div className="grid gap-3 @md/col:grid-cols-2 @2xl/col:grid-cols-3">
        {props.images.map((img, i) =>
          img.url ? (
            <img
              key={i}
              src={img.url}
              alt={img.alt}
              className="aspect-[4/3] w-full rounded-md object-cover"
            />
          ) : (
            <ImagePlaceholder key={i} alt={img.alt} />
          ),
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Contact (complex)
// =====================================================================

function ContactBlock({
  block,
}: {
  block: Extract<Block, { type: 'contact' }>;
}) {
  const props = block.props;
  return (
    <div className="grid gap-6 @md/col:gap-8 @lg/col:grid-cols-2">
      <div className="space-y-3">
        {props.title ? (
          <h2 className="text-xl @md/col:text-2xl font-semibold tracking-tight">{props.title}</h2>
        ) : null}
        {props.description ? (
          <p className="text-sm @md/col:text-base leading-relaxed text-muted-foreground">{props.description}</p>
        ) : null}
        <dl className="space-y-2 pt-2 text-sm">
          {props.address ? (
            <div>
              <dt className="font-medium">Address</dt>
              <dd className="text-muted-foreground">{props.address}</dd>
            </div>
          ) : null}
          {props.phone ? (
            <div>
              <dt className="font-medium">Phone</dt>
              <dd className="text-muted-foreground">{props.phone}</dd>
            </div>
          ) : null}
          {props.email ? (
            <div>
              <dt className="font-medium">Email</dt>
              <dd className="text-muted-foreground">{props.email}</dd>
            </div>
          ) : null}
        </dl>
      </div>
      {props.formEnabled ? (
        <div className="space-y-3 rounded-lg border border-border-subtle bg-card p-5">
          <div className="text-sm font-medium">Send a message</div>
          <div className="h-10 rounded-md border border-border bg-muted/30" />
          <div className="h-10 rounded-md border border-border bg-muted/30" />
          <div className="h-24 rounded-md border border-border bg-muted/30" />
          <PrimaryPill>Send</PrimaryPill>
        </div>
      ) : null}
    </div>
  );
}

// =====================================================================
// About
// =====================================================================

type AboutProps = Extract<Block, { type: 'about' }>['props'];

function AboutBlock({
  block,
  editable,
  onChange,
}: {
  block: Extract<Block, { type: 'about' }>;
  editable: boolean;
  onChange: BlockPreviewProps['onChange'];
}) {
  const props = block.props;
  const update = (patch: Partial<AboutProps>) => onChange?.({ ...props, ...patch });
  return (
    <div className="grid items-center gap-6 @md/col:gap-8 @lg/col:grid-cols-2">
      <div className="space-y-3">
        {props.title || editable ? (
          editable ? (
            <EditableText
              value={props.title ?? ''}
              onChange={(v) => update({ title: v || undefined })}
              placeholder="About us"
              className="text-xl @md/col:text-2xl font-semibold tracking-tight"
            />
          ) : (
            <h2 className="text-xl @md/col:text-2xl font-semibold tracking-tight">{props.title}</h2>
          )
        ) : null}

        {editable ? (
          <EditableText
            value={props.body}
            onChange={(v) => update({ body: v })}
            placeholder="Tell your story."
            multiline
            className="whitespace-pre-line text-sm @md/col:text-base leading-relaxed text-muted-foreground"
          />
        ) : (
          <p className="whitespace-pre-line text-sm @md/col:text-base leading-relaxed text-muted-foreground">
            {props.body}
          </p>
        )}
      </div>
      {props.image ? (
        props.image.url ? (
          <img
            src={props.image.url}
            alt={props.image.alt}
            className="aspect-[4/3] w-full rounded-lg object-cover"
          />
        ) : (
          <ImagePlaceholder alt={props.image.alt} />
        )
      ) : null}
    </div>
  );
}

// =====================================================================
// Testimonials (complex)
// =====================================================================

function TestimonialsBlock({
  block,
}: {
  block: Extract<Block, { type: 'testimonials' }>;
}) {
  const props = block.props;
  return (
    <div className="space-y-6">
      {props.title ? (
        <h2 className="text-center text-xl @md/col:text-2xl font-semibold tracking-tight">{props.title}</h2>
      ) : null}
      <div className="grid gap-5 @md/col:grid-cols-2 @2xl/col:grid-cols-3">
        {props.items.map((t, i) => (
          <figure key={i} className="rounded-lg border border-border-subtle bg-card p-5">
            <blockquote className="text-sm italic leading-relaxed text-foreground">
              "{t.quote}"
            </blockquote>
            <figcaption className="mt-4 flex items-center gap-3">
              {t.avatar?.url ? (
                <img src={t.avatar.url} alt="" className="h-9 w-9 rounded-full object-cover" />
              ) : (
                <div className="h-9 w-9 rounded-full bg-muted" />
              )}
              <div>
                <div className="text-sm font-medium">{t.author}</div>
                {t.role ? <div className="text-xs text-muted-foreground">{t.role}</div> : null}
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// CTA
// =====================================================================

type CtaProps = Extract<Block, { type: 'cta' }>['props'];

function CtaBlock({
  block,
  editable,
  onChange,
}: {
  block: Extract<Block, { type: 'cta' }>;
  editable: boolean;
  onChange: BlockPreviewProps['onChange'];
}) {
  const props = block.props;
  const update = (patch: Partial<CtaProps>) => onChange?.({ ...props, ...patch });
  const onBrand = props.background === 'brand';
  return (
    <div
      className={
        'rounded-xl px-4 py-6 @md/col:px-6 @md/col:py-10 text-center ' +
        (onBrand ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground')
      }
    >
      {editable ? (
        <EditableText
          value={props.headline}
          onChange={(v) => update({ headline: v })}
          placeholder="Ready to get started?"
          className={
            'block text-xl @md/col:text-2xl @2xl/col:text-3xl font-semibold tracking-tight ' +
            (onBrand ? 'text-primary-foreground' : 'text-foreground')
          }
        />
      ) : (
        <h2 className="text-xl @md/col:text-2xl @2xl/col:text-3xl font-semibold tracking-tight">{props.headline}</h2>
      )}
      {props.subhead || editable ? (
        editable ? (
          <EditableText
            value={props.subhead ?? ''}
            onChange={(v) => update({ subhead: v || undefined })}
            placeholder="A line of supporting context."
            multiline
            className={
              'mx-auto mt-3 block max-w-prose text-base leading-relaxed ' +
              (onBrand ? 'text-primary-foreground/85' : 'text-muted-foreground')
            }
          />
        ) : (
          <p
            className={
              'mx-auto mt-3 max-w-prose text-base leading-relaxed ' +
              (onBrand ? 'text-primary-foreground/80' : 'text-muted-foreground')
            }
          >
            {props.subhead}
          </p>
        )
      ) : null}
      <div className="mt-6">
        {editable ? (
          <EditableText
            value={props.primaryCta.label}
            onChange={(v) => update({ primaryCta: { ...props.primaryCta, label: v } })}
            placeholder="Get started"
            className={
              'inline-flex items-center rounded-md px-5 py-2.5 text-sm font-semibold ' +
              (onBrand ? 'bg-card text-primary' : 'bg-primary text-primary-foreground')
            }
          />
        ) : (
          <span
            className={
              'inline-flex items-center rounded-md px-5 py-2.5 text-sm font-semibold ' +
              (onBrand ? 'bg-card text-primary' : 'bg-primary text-primary-foreground')
            }
          >
            {props.primaryCta.label}
          </span>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Heading
// =====================================================================

type HeadingProps = Extract<Block, { type: 'heading' }>['props'];

function HeadingBlock({
  block,
  editable,
  onChange,
}: {
  block: Extract<Block, { type: 'heading' }>;
  editable: boolean;
  onChange: BlockPreviewProps['onChange'];
}) {
  const props = block.props;
  const update = (patch: Partial<HeadingProps>) => onChange?.({ ...props, ...patch });
  const Tag = props.level === 3 ? 'h3' : props.level === 4 ? 'h4' : 'h2';
  const sizeClass =
    props.level === 4
      ? 'text-base @md/col:text-lg'
      : props.level === 3
      ? 'text-lg @md/col:text-xl'
      : 'text-xl @md/col:text-2xl';
  const alignClass =
    props.align === 'center' ? 'text-center' : props.align === 'right' ? 'text-right' : 'text-left';

  if (editable) {
    return (
      <EditableText
        value={props.text}
        onChange={(v) => update({ text: v })}
        placeholder="Section heading"
        className={'font-semibold tracking-tight ' + sizeClass + ' ' + alignClass}
      />
    );
  }
  return <Tag className={'font-semibold tracking-tight ' + sizeClass + ' ' + alignClass}>{props.text}</Tag>;
}

// =====================================================================
// Text
// =====================================================================

type TextProps = Extract<Block, { type: 'text' }>['props'];

function TextBlock({
  block,
  editable,
  onChange,
}: {
  block: Extract<Block, { type: 'text' }>;
  editable: boolean;
  onChange: BlockPreviewProps['onChange'];
}) {
  const props = block.props;
  const update = (patch: Partial<TextProps>) => onChange?.({ ...props, ...patch });
  const alignClass =
    props.align === 'center' ? 'text-center mx-auto max-w-prose' : 'text-left';

  if (editable) {
    return (
      <EditableText
        value={props.body}
        onChange={(v) => update({ body: v })}
        placeholder="Write here…"
        multiline
        className={'whitespace-pre-line text-sm @md/col:text-base leading-relaxed text-foreground ' + alignClass}
      />
    );
  }
  return (
    <p className={'whitespace-pre-line text-sm @md/col:text-base leading-relaxed text-foreground ' + alignClass}>
      {props.body}
    </p>
  );
}

// =====================================================================
// FAQ
// =====================================================================

type FaqProps = Extract<Block, { type: 'faq' }>['props'];

function FaqBlock({
  block,
  editable,
  onChange,
}: {
  block: Extract<Block, { type: 'faq' }>;
  editable: boolean;
  onChange: BlockPreviewProps['onChange'];
}) {
  const props = block.props;
  const updateItem = (idx: number, patch: Partial<FaqProps['items'][number]>) => {
    if (!onChange) return;
    const items = props.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange({ ...props, items });
  };
  const updateTitle = (next: string) => {
    if (!onChange) return;
    onChange({ ...props, title: next || undefined });
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      {props.title || editable ? (
        editable ? (
          <EditableText
            value={props.title ?? ''}
            onChange={updateTitle}
            placeholder="Frequently asked questions"
            className="block text-center text-xl @md/col:text-2xl font-semibold tracking-tight"
          />
        ) : (
          <h2 className="text-center text-xl @md/col:text-2xl font-semibold tracking-tight">{props.title}</h2>
        )
      ) : null}
      <div className="divide-y divide-border-subtle rounded-lg border border-border-subtle bg-card">
        {props.items.map((item, i) => (
          <div key={i} className="px-5 py-4">
            {editable ? (
              <>
                <EditableText
                  value={item.question}
                  onChange={(v) => updateItem(i, { question: v })}
                  placeholder="What's a question your visitors ask?"
                  className="block font-medium"
                />
                <EditableText
                  value={item.answer}
                  onChange={(v) => updateItem(i, { answer: v })}
                  placeholder="Answer it clearly here."
                  multiline
                  className="mt-2 block text-sm leading-relaxed text-muted-foreground"
                />
              </>
            ) : (
              <>
                <div className="font-medium">{item.question}</div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.answer}</p>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// Team (complex)
// =====================================================================

function TeamBlock({
  block,
}: {
  block: Extract<Block, { type: 'team' }>;
}) {
  const props = block.props;
  return (
    <div className="space-y-6">
      {props.title ? (
        <h2 className="text-center text-xl @md/col:text-2xl font-semibold tracking-tight">{props.title}</h2>
      ) : null}
      <div className="grid gap-6 @md/col:grid-cols-2 @2xl/col:grid-cols-3">
        {props.members.map((m, i) => (
          <div key={i} className="text-center">
            {m.photo?.url ? (
              <img
                src={m.photo.url}
                alt={m.photo.alt}
                className="mx-auto h-24 w-24 rounded-full object-cover"
              />
            ) : (
              <div className="mx-auto h-24 w-24 rounded-full bg-muted" />
            )}
            <div className="mt-3 text-sm font-semibold">{m.name}</div>
            {m.role ? <div className="text-xs text-muted-foreground">{m.role}</div> : null}
            {m.bio ? (
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{m.bio}</p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// Credentials (trust strip) — horizontal row of icon + label chips.
// Editing happens in the right-side inspector (3B-3d.a).
// =====================================================================

function CredentialsBlock({
  block,
}: {
  block: Extract<Block, { type: 'credentials' }>;
}) {
  const props = block.props;
  return (
    <div className="space-y-3">
      <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
        {props.items.map((item, i) => (
          <li key={i} className="inline-flex items-center gap-2">
            {item.icon ? (
              <IconPreview value={item.icon} className="h-4 w-4 text-brand-700 shrink-0 [&_span]:text-base [&_span]:leading-none" />
            ) : null}
            <span className="font-medium text-foreground">{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// =====================================================================
// Locations (multi-location phase, Task 9 — Scenario B). Member NAP is
// resolved server-side at render time (never stored in props, never fetched
// live by the editor), so the preview is an illustrative placeholder card —
// not real data. Heading is inline-editable like other simple text blocks.
// =====================================================================

function LocationsBlock({
  block,
}: {
  block: Extract<Block, { type: 'locations' }>;
}) {
  const props = block.props;
  const placeholders = ['Your other location', 'Another location'];
  return (
    <div className="space-y-6">
      <h2 className="text-center text-xl @md/col:text-2xl font-semibold tracking-tight">{props.heading}</h2>
      <div className="grid gap-5 @md/col:grid-cols-2">
        {placeholders.map((label, i) => (
          <div key={i} className="rounded-lg border border-dashed border-border-subtle bg-card p-5 text-center">
            <div className="text-sm font-semibold text-muted-foreground">{label}</div>
            <div className="mt-1 text-xs text-muted-foreground">Address pulled from that location's business info</div>
            {props.showPhones ? (
              <div className="mt-1 text-xs text-muted-foreground">Phone shown when available</div>
            ) : null}
          </div>
        ))}
      </div>
      <p className="text-center text-xs text-muted-foreground">
        Live on your published site — each card shows the real name, address, and phone from that
        location's business info.
      </p>
    </div>
  );
}

// =====================================================================
// Footer
// =====================================================================

type FooterProps = Extract<Block, { type: 'footer' }>['props'];

function FooterBlock({
  block,
  editable,
  onChange,
}: {
  block: Extract<Block, { type: 'footer' }>;
  editable: boolean;
  onChange: BlockPreviewProps['onChange'];
}) {
  const props = block.props;
  const update = (patch: Partial<FooterProps>) => onChange?.({ ...props, ...patch });
  const year = props.copyrightYear ?? new Date().getFullYear();
  return (
    <div className="rounded-lg bg-muted/50 px-6 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          {editable ? (
            <EditableText
              value={props.businessName}
              onChange={(v) => update({ businessName: v })}
              placeholder="Your business name"
              className="block text-base font-semibold"
            />
          ) : (
            <div className="text-base font-semibold">{props.businessName}</div>
          )}
          {props.tagline || editable ? (
            editable ? (
              <EditableText
                value={props.tagline ?? ''}
                onChange={(v) => update({ tagline: v || undefined })}
                placeholder="A short tagline"
                className="mt-1 block text-sm text-muted-foreground"
              />
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">{props.tagline}</p>
            )
          ) : null}
        </div>
        {props.links.length > 0 ? (
          <ul className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
            {props.links.map((l, i) => (
              <li key={i}>{l.label}</li>
            ))}
          </ul>
        ) : null}
      </div>
      <p className="mt-6 border-t border-border-subtle pt-4 text-xs text-muted-foreground">
        © {year} {props.businessName}
      </p>
    </div>
  );
}
